import React, { useEffect, useMemo, useState } from "react";
import {
	Typography,
	Input,
	List,
	Badge,
	Table,
	Space,
	Empty,
	Spin,
	Statistic,
	Row,
	Col,
	Button,
	Checkbox,
	Pagination,
} from "antd";
import {
	SearchOutlined,
	ShopOutlined,
	DatabaseOutlined,
	StarOutlined,
	ReloadOutlined,
} from "@ant-design/icons";
import {
	Body,
	Content,
	HeaderBar,
	KpiCard,
	Pill,
	PosItem,
	RightCard,
	Sider,
} from "./styles";
import {
	get_laboratory_suggested_products,
	update_suggested_product_state,
} from "../../../../../api/endpoints/laboratorySuggestions";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Main from "../../../../layout/main/Main";
import { useAuth } from "../../../../../contexts/authContext";
import { COLORS } from "../../../../layout/theme";

const { Title, Text } = Typography;

const FLAGS = {
	OOS: 1,
	RECENCY: 2,
	FREQUENCY: 4,
	CURATED: 8,
	RECOVERY: 16,
};

function decodeFlagsToTags(flags, sourceFlagsText) {
	if (sourceFlagsText) {
		const parts = sourceFlagsText.split("|").filter(Boolean);
		return parts.map((p, idx) => {
			const key = p.toUpperCase();
			let color = "default";
			let label = p;

			switch (key) {
				case "OOS":
					color = "red";
					label = "Pendiente";
					break;
				case "RECENCY":
					color = "geekblue";
					label = "Recencia";
					break;
				case "FREQUENCY":
					color = "green";
					label = "Frecuencia";
					break;
				case "CURATED":
					color = "gold";
					label = "Destacado";
					break;
				case "RECOVERY":
					color = "purple";
					label = "Recupero";
					break;
				default:
					break;
			}
			return (
				<Pill key={idx} color={color}>
					{label}
				</Pill>
			);
		});
	}
	return [];
}

function groupByPos(suggestedProducts) {
	const map = new Map();
	for (const s of suggestedProducts || []) {
		const key = s.pointOfSaleId || s.PointOfSaleId;
		const pos = s.pointOfSale || s.PointOfSale || {};
		if (!map.has(key)) map.set(key, { pos, items: [] });
		map.get(key).items.push(s);
	}
	return Array.from(map.entries()).map(([id, v]) => ({
		id,
		...v.pos,
		items: v.items,
	}));
}

export default function SuggestedProductsDashboard() {
	const { id } = useParams();
	const { userdata } = useAuth();
	const [searchParams] = useSearchParams();
	const uri = searchParams.get("uri");

	const [loading, setLoading] = useState(true);
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);

	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [totalCount, setTotalCount] = useState(0);

	const [selectedPosId, setSelectedPosId] = useState(null);
	const [flag, setFlag] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(searchTerm);
			setPage(1);
		}, 600);
		return () => clearTimeout(timer);
	}, [searchTerm]);

	const fetchData = async () => {
		try {
			setLoading(true);
			setError(null);

			const res = await get_laboratory_suggested_products(id, {
				pageNumber: page,
				pageSize: pageSize,
				searchTerm: debouncedSearch,
			});

			setData(res);
			setTotalCount(res.totalCount || res.TotalCount || 0);

			const currentList = res.suggestedProducts || res.SuggestedProducts || [];
			const grouped = groupByPos(currentList);

			if (!selectedPosId || !grouped.find((g) => g.id === selectedPosId)) {
				setSelectedPosId(grouped[0]?.id || null);
			}
		} catch (e) {
			setError(e.message || "Error al cargar datos");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (id) fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id, flag, page, debouncedSearch, pageSize]);

	const curatedProducts = data?.curatedProducts || data?.CuratedProducts || [];
	const suggestionsMeta = data?.suggestions || data?.Suggestions;

	const groupedByPosList = useMemo(
		() => groupByPos(data?.suggestedProducts || data?.SuggestedProducts),
		[data],
	);

	const selectedPos = useMemo(
		() => groupedByPosList.find((p) => p.id === selectedPosId) || null,
		[groupedByPosList, selectedPosId],
	);

	const handleSuggestedState = async (e, r) => {
		try {
			const payload = {
				productId: r.productId,
				laboratoryId: r.laboratoryId,
				pointOfSaleId: r.pointOfSaleId,
				runId: r.runId,
				state: !e.target.checked,
			};
			await update_suggested_product_state(payload);
			setFlag((prev) => !prev);
		} catch (error) {
			console.error(error);
		}
	};

	const handleSendSuggested = () => {
		console.log("Enviando...", selectedPos?.items);
	};

	const columns = [
		{
			title: "#",
			dataIndex: "rankOrder",
			key: "rank",
			width: 60,
			render: (_, r) => (
				<Text type="secondary">{r.rankOrder ?? r.RankOrder}</Text>
			),
		},
		{
			title: "Producto",
			key: "product",
			render: (_, r) => {
				const p = r.product || r.Product || {};
				return (
					<Space>
						<img
							src={p.photoUrl || p.PhotoUrl}
							alt="img"
							style={{
								width: 40,
								height: 40,
								objectFit: "cover",
								borderRadius: 8,
							}}
							onError={(e) => (e.currentTarget.style.visibility = "hidden")}
						/>
						<div>
							<Text strong>{p.title || p.Title}</Text>
							<div style={{ fontSize: 12, color: "#6b7280" }}>
								EAN: {r.ean13 || r.Ean13}
							</div>
						</div>
					</Space>
				);
			},
		},
		{
			title: "Cant.",
			dataIndex: "suggestedQty",
			key: "qty",
			width: 80,
			align: "right",
			render: (v, r) => v ?? r.SuggestedQty,
		},
		{
			title: "Score",
			dataIndex: "score",
			key: "score",
			width: 80,
			align: "right",
			render: (v, r) => (v ?? r.Score)?.toFixed(1),
		},
		{
			title: "Motivo",
			key: "flags",
			render: (_, r) =>
				decodeFlagsToTags(
					r.sourceFlags ?? r.SourceFlags,
					r.sourceFlagsText ?? r.SourceFlagsText,
				),
		},
		{
			title: "",
			key: "action",
			width: 50,
			render: (_, r) => (
				<Checkbox
					checked={!r.deleted}
					onChange={(e) => handleSuggestedState(e, r)}
				/>
			),
		},
	];

	return (
		<Main>
			<Main.Header>
				<Main.Breadcrumb>
					<Main.BreadcrumbItem>
						{uri ? (
							<Link to={"/generalConfig"}>Configuración General</Link>
						) : (
							<Link to="/">Home</Link>
						)}
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.BreadcrumbItem>
						<Link to={"/admin/suggestedProducts?uri=" + uri}>
							Listado clientes
						</Link>
					</Main.BreadcrumbItem>
					<Main.BreadcrumbSeparator />
					<Main.ActiveBreadcrumbItem>
						Listado de sugeridos
					</Main.ActiveBreadcrumbItem>
				</Main.Breadcrumb>
			</Main.Header>
			<HeaderBar>
				<Space size="large" align="center">
					<Title level={4} style={{ margin: 0 }}>
						Sugeridos{" "}
						{suggestionsMeta?.laboratoryName
							? ` - ${suggestionsMeta.laboratoryName}`
							: ""}
					</Title>
				</Space>
			</HeaderBar>

			<Body>
				<Sider
					width={380}
					style={{ display: "flex", flexDirection: "column", height: "97%" }}
				>
					<div
						style={{
							paddingTop: 10,
							paddingBottom: 8,
							display: "flex",
							alignItems: "center",
						}}
					>
						<Input
							allowClear
							prefix={<SearchOutlined />}
							placeholder="Buscar por CUIT, Nombre o Razon social"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							style={{ marginBottom: 10 }}
						/>
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								paddingBottom: 10,
							}}
						>
							<Button
								type="text"
								size="small"
								icon={<ReloadOutlined />}
								onClick={fetchData}
								loading={loading}
							/>
						</div>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-start",
							alignItems: "center",
							paddingBottom: 12,
						}}
					>
						<Text type="secondary" style={{ fontSize: 12 }}>
							{totalCount > 0 ? `Total: ${totalCount} puntos de venta` : ""}
						</Text>
					</div>

					<div style={{ flex: 1, overflowY: "auto", height: "80%" }}>
						<List
							dataSource={groupedByPosList}
							loading={loading}
							split={false}
							renderItem={(pos) => (
								<List.Item
									style={{
										padding: "8px 12px",
										cursor: "pointer",
										borderRadius: 8,
										marginBottom: 4,
										background:
											pos.id === selectedPosId ? "#e6f7ff" : "transparent",
										border:
											pos.id === selectedPosId
												? "1px solid #91caff"
												: "1px solid transparent",
									}}
									onClick={() => setSelectedPosId(pos.id)}
								>
									<PosItem>
										<Badge
											count={pos.items.length}
											color={
												pos.id === selectedPosId ? COLORS.Primary : COLORS.Grey
											}
										>
											<ShopOutlined
												style={{
													fontSize: 20,
													color:
														pos.id === selectedPosId
															? COLORS.Primary
															: COLORS.MediumGrey,
													marginTop: 4,
												}}
											/>
										</Badge>
										<div style={{ marginLeft: 12, width: "100%" }}>
											<div
												style={{
													fontWeight: 600,
													color:
														pos.id === selectedPosId
															? COLORS.Primary
															: COLORS.MediumGrey,
												}}
											>
												{pos.Name || pos.name}
											</div>
											<div
												style={{
													color:
														pos.id === selectedPosId
															? COLORS.Primary
															: COLORS.MediumGrey,
													fontSize: 11,
													lineHeight: 1.2,
												}}
											>
												{pos.Cuit || pos.cuit}
											</div>
											<div
												style={{
													color:
														pos.id === selectedPosId
															? COLORS.Primary
															: COLORS.MediumGrey,
													fontSize: 11,
												}}
											>
												{pos.Address || ""}
											</div>
										</div>
									</PosItem>
								</List.Item>
							)}
							locale={{
								emptyText: (
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description="Sin resultados"
									/>
								),
							}}
						/>
					</div>

					<div
						style={{
							paddingTop: 30,
							borderTop: `1px solid ${COLORS.White}`,
							textAlign: "center",
						}}
					>
						<Pagination
							simple
							current={page}
							pageSize={pageSize}
							total={totalCount}
							onChange={(p) => setPage(p)}
							size="small"
						/>
					</div>
				</Sider>

				<Content>
					{loading && !data ? (
						<div
							style={{ display: "grid", placeItems: "center", height: "100%" }}
						>
							<Spin tip="Cargando datos..." size="large" />
						</div>
					) : error ? (
						<Empty description={<Text type="danger">{error}</Text>} />
					) : !selectedPos ? (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								height: "100%",
								flexDirection: "column",
							}}
						>
							<ShopOutlined
								style={{
									fontSize: 48,
									color: COLORS.LightGrey,
									marginBottom: 16,
								}}
							/>
							<Text type="secondary">
								Selecciona un punto de venta para ver detalles
							</Text>
						</div>
					) : (
						<Space
							direction="vertical"
							size="large"
							style={{ display: "flex", width: "100%" }}
						>
							<Row gutter={16}>
								<Col xs={24} lg={12}>
									<KpiCard>
										<Statistic
											title="Punto de Venta"
											value={selectedPos.name}
											prefix={<ShopOutlined />}
											valueStyle={{ fontSize: 16, fontWeight: "bold" }}
										/>
										<Text type="secondary" style={{ fontSize: 12 }}>
											{selectedPos.cuit} | {selectedPos.address}
										</Text>
									</KpiCard>
								</Col>
								<Col xs={12} lg={6}>
									<KpiCard>
										<Statistic
											title="Items Sugeridos"
											value={selectedPos.items.length}
											prefix={<DatabaseOutlined />}
										/>
									</KpiCard>
								</Col>
								<Col xs={12} lg={6}>
									<KpiCard>
										<Statistic
											title="Destacados Activos"
											value={curatedProducts.length}
											prefix={<StarOutlined />}
										/>
									</KpiCard>
								</Col>
							</Row>

							<RightCard
								title="Sugeridos"
								extra={
									!userdata.isOneMarket && (
										<Button
											type="primary"
											shape="round"
											onClick={handleSendSuggested}
										>
											Procesar Pedido
										</Button>
									)
								}
							>
								<Table
									rowKey={(r) => r.productId || r.ProductId}
									columns={columns}
									dataSource={selectedPos.items}
									pagination={false}
									scroll={{ y: 500 }}
									size="small"
								/>
							</RightCard>
						</Space>
					)}
				</Content>
			</Body>
		</Main>
	);
}
